/**
 * =============================================================================
 * LOS TRES KPI DE UNA SESION — DE DONDE SALEN
 * =============================================================================
 * Hay dos formas de sacarlos, y la diferencia es de fondo:
 *
 *   **v3 (`derived.core`)** — el server ya lo calculo. Se lee y se pinta. Los
 *   numeros significan lo mismo en TODAS las experiencias, asi que la sesion de
 *   hoy en una actividad cognitiva se puede comparar con la del mes pasado en
 *   una de motricidad.
 *
 *   **legacy** — hay que adivinar. Se busca `totalFaults`, si no la metrica que
 *   el catalogo declare, si no la primera clave numerica que aparezca. Cada
 *   experiencia dice lo que quiere y la columna significa una cosa distinta
 *   segun la fila.
 *
 * Se conservan las dos porque las experiencias antiguas siguen vivas hasta que
 * se retiren. Pero la de arriba es la buena, y cuando `derived` esta, manda.
 *
 * POR QUE ES UNA FUNCION PURA Y NO ESTA DENTRO DEL COMPONENTE: para poder
 * congelar con tests cual de los dos caminos se toma. Esa decision se rompe con
 * facilidad y el sintoma —una columna que significa otra cosa— no se ve en
 * pantalla.
 * =============================================================================
 */

/** Un valor que se puede pintar como numero. */
export function esNumerico(v) {
  if (typeof v === "number" && Number.isFinite(v)) return true;
  if (typeof v === "string" && v !== "" && !Number.isNaN(Number(v)))
    return true;
  return false;
}

/** El guion que ya usa el resto del portal para "no hay dato". */
export const SIN_DATO = "—";

/** true si esta sesion trae la capa que calcula el server. */
export function tieneNucleo(session) {
  return !!(session && session.derived && session.derived.core);
}

/**
 * Color del acento segun como termino.
 *
 * `abandoned` NO es un error: es un dato clinico. Por eso va en ambar y no en
 * rojo — el rojo en una pantalla clinica dice "algo ha fallado", y que un
 * paciente deje una actividad a medias no es un fallo, es informacion.
 */
export function acentoDeFinal(endReason) {
  switch (endReason) {
    case "finished":
      return "success";
    case "abandoned":
    case "timeout":
      return "warning";
    case "error":
      return "danger";
    default:
      return "info";
  }
}

/**
 * Los tres KPI de la cabecera.
 *
 * @param {object} session   la sesion tal cual la devuelve la API
 * @param {object} opciones
 * @param {Function} opciones.t         traductor del namespace motricity
 * @param {Function} opciones.duracion  formateador de segundos
 * @param {object} [opciones.game]      la entrada del catalogo, solo legacy
 * @param {Function} [opciones.humanizar] para nombres de campo legacy
 * @returns {{fuente: "core"|"legacy", kpis: Array}}
 */
export function kpisDeSesion(session, { t, duracion, game, humanizar }) {
  /*
   * LA DURACION BUENA ES LA DEL SERVIDOR, NO LA QUE MANDA LA EXPERIENCIA.
   *
   * Aqui se leia `session.durationSeconds`, que es el numero que envia el
   * juego. Medido en produccion el 2026-08-11, las cuatro sesiones v3 que hay
   * lo mandan a CERO:
   *
   *     A9ZK   durationSeconds crudo 0   derived.durationSeconds 175
   *     GPC3   durationSeconds crudo 0   derived.durationSeconds 410
   *
   * Y el terapeuta veia "0 min 00 s" en una sesion de casi tres minutos. El
   * dato correcto estaba ahi al lado: el derivador lo calcula desde
   * `startedAt`/`finishedAt` justo porque el del cliente no es de fiar, y
   * despues la pantalla tiraba ese calculo y pintaba el cero.
   *
   * Se prefiere el del servidor y se cae al del cliente solo en las sesiones
   * legacy, que no tienen capa derivada.
   */
  const delServidor = session?.derived?.durationSeconds;
  const segundos = Number(
    typeof delServidor === "number" && Number.isFinite(delServidor)
      ? delServidor
      : session?.durationSeconds || 0,
  );
  const kpiDuracion = {
    id: "duracion",
    title: t("sessionView.kpi.duration"),
    value: duracion(segundos),
    accent: "info",
  };

  if (tieneNucleo(session)) {
    const core = session.derived.core;

    // La puntuacion normalizada es LA metrica comparable. Va primera porque es
    // la unica que significa lo mismo en dos experiencias distintas.
    const kpiPuntuacion = {
      id: "puntuacion",
      title: t("sessionView.kpi.puntuacion"),
      value: core.score === null ? SIN_DATO : core.score,
      suffix: core.score === null ? "" : "/ 100",
      hint:
        core.scoreRaw !== null && core.scoreMax !== null
          ? t("sessionView.kpi.hintPuntuacion", {
              crudo: core.scoreRaw,
              maximo: core.scoreMax,
            })
          : undefined,
      // Sin umbrales clinicos: nadie ha fijado que puntuacion es "buena" en una
      // experiencia VR. Pintarlo verde o rojo seria inventarse un baremo.
      accent: "info",
    };

    // De los tres contadores se pinta el que la experiencia mida, en este
    // orden. null significa que NO lo mide, y entonces no se pinta un cero:
    // se pasa al siguiente. Un cero falso en una pantalla clinica es peor que
    // un hueco honesto.
    const candidatos = [
      { clave: "errors", titulo: "sessionView.kpi.errores", acento: "danger" },
      {
        clave: "successes",
        titulo: "sessionView.kpi.aciertos",
        acento: "success",
      },
      { clave: "assists", titulo: "sessionView.kpi.ayudas", acento: "warning" },
    ];
    const medido = candidatos.find(
      (c) => core[c.clave] !== null && core[c.clave] !== undefined,
    );

    const kpiContador = medido
      ? {
          id: medido.clave,
          title: t(medido.titulo),
          value: core[medido.clave],
          // Cero errores es una buena noticia, no una alarma.
          accent:
            medido.clave === "errors" && core[medido.clave] === 0
              ? "success"
              : medido.acento,
        }
      : {
          // Ninguno de los tres. Es correcto para una experiencia de
          // relajacion o de exploracion, y se dice en vez de dejar un hueco
          // mudo que parece un fallo.
          id: "sinContadores",
          title: t("sessionView.kpi.sinContadores"),
          value: SIN_DATO,
          hint: t("sessionView.kpi.sinContadoresAyuda"),
          accent: "info",
        };

    return { fuente: "core", kpis: [kpiPuntuacion, kpiContador, kpiDuracion] };
  }

  // --- Camino legacy ---------------------------------------------------------
  const summary = session?.summary || {};
  const fases = Array.isArray(session?.phases) ? session.phases : [];

  let claveMetrica = null;
  if (Object.prototype.hasOwnProperty.call(summary, "totalFaults")) {
    claveMetrica = "totalFaults";
  } else if (
    game?.primaryMetric &&
    Object.prototype.hasOwnProperty.call(summary, game.primaryMetric)
  ) {
    claveMetrica = game.primaryMetric;
  }
  const valorMetrica =
    claveMetrica && esNumerico(summary[claveMetrica])
      ? Number(summary[claveMetrica])
      : null;

  const totalFases = esNumerico(summary.totalPhases)
    ? Number(summary.totalPhases)
    : fases.length;
  const fasesHechas = esNumerico(summary.phasesCompleted)
    ? Number(summary.phasesCompleted)
    : fases.length;

  const titulo =
    claveMetrica && claveMetrica !== "totalFaults" && humanizar
      ? humanizar(claveMetrica)
      : t("sessionView.kpi.totalFaults");

  return {
    fuente: "legacy",
    kpis: [
      {
        id: "metricaPrincipal",
        title: titulo,
        value: valorMetrica !== null ? valorMetrica : SIN_DATO,
        hint: game?.label
          ? t("sessionView.kpi.hintMetricaPrincipal", { juego: game.label })
          : undefined,
        accent:
          valorMetrica === 0
            ? "success"
            : valorMetrica !== null
              ? "danger"
              : "info",
      },
      {
        id: "fases",
        title: t("sessionView.kpi.phasesCompleted"),
        value: fasesHechas,
        suffix: totalFases ? `/ ${totalFases}` : "",
        accent:
          totalFases && fasesHechas === totalFases ? "success" : "warning",
      },
      kpiDuracion,
    ],
  };
}
